import { useEffect } from "react";
import { type TargetCollaborationListItem, useGroupCollaborationsPaginated } from "@/api/collaborations";

/**
 * Public state of an expanded Smart Unbox group, surfaced to the explorer
 * and the table. Owned by `GroupExpansionController`; the table reads
 * `items` to render rows and calls `fetchNextPage` from its scroll sentinel.
 */
export type GroupExpansionState = {
  items: TargetCollaborationListItem[];
  total: number;
  isInitialLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
};

type Props = {
  groupId: string;
  onChange: (groupId: string, state: GroupExpansionState | null) => void;
};

/**
 * Renderless owner of `useGroupCollaborationsPaginated` for a single Smart
 * Unbox group. Mounts when the user expands the group and unmounts on
 * collapse, so each open group has its own infinite query without violating
 * React's hook-call rules. Reports state to the parent via `onChange` and
 * clears itself on unmount, allowing the parent to react to scroll-driven
 * page loads (UNB-1371).
 */
export const GroupExpansionController = ({ groupId, onChange }: Props) => {
  const query = useGroupCollaborationsPaginated(groupId);

  // Push state up whenever the underlying infinite query changes. React
  // Query keeps `data`, `fetchNextPage`, etc. structurally stable, so this
  // effect only re-runs when something the parent actually cares about
  // changed — no render loop.
  useEffect(() => {
    const items = query.data?.pages.flatMap((p) => p.collaborations) ?? [];
    const total = query.data?.pages[0]?.total ?? 0;
    onChange(groupId, {
      items,
      total,
      isInitialLoading: query.isLoading,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: !!query.hasNextPage,
      fetchNextPage: query.fetchNextPage,
    });
  }, [
    groupId,
    onChange,
    query.data,
    query.isLoading,
    query.isFetchingNextPage,
    query.hasNextPage,
    query.fetchNextPage,
  ]);

  // Clear the parent's entry on unmount (group collapsed).
  useEffect(() => {
    return () => onChange(groupId, null);
  }, [groupId, onChange]);

  return null;
};
